import {colors, radii, tokens, headerSizes} from '@commercial-helios/ui/theme'
import {
    Trigger,
    Portal,
    Content,
    Viewport,
    Item,
    Value,
    ItemText,
    Icon,
    Root,
} from '@radix-ui/react-select'
import type {PropsWithChildren} from 'react'
import React from 'react'
import styled from 'styled-components'

import chevronUp from '@helios/shared/images/icons/chevron-up.svg'
import {Text, text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'

type Option<TValue extends string> = {
    value: TValue
    label: string | JSX.Element
}

interface Props<TValue extends string> {
    onChange: (newValue: TValue) => void
    options: Option<TValue>[]
    triggerText?: string
    value: string
}

const Dropdown = <TValue extends string>({
    onChange,
    options,
    triggerText,
    value,
}: PropsWithChildren<Props<TValue>>) => (
    <Root value={value} onValueChange={onChange}>
        <StyledListboxButton>
            <Text variant="heading-base" as="span">
                {triggerText}{' '}
                <Text variant="heading-base" as="span" color="brand">
                    <Value />
                </Text>
            </Text>
            <Icon>
                <Arrow />
            </Icon>
        </StyledListboxButton>

        <Portal>
            <StyledListboxPopover
                position="popper"
                sideOffset={20}
                collisionPadding={collisionPaddingStyles}
            >
                <StyledViewport>
                    {options.map((option, i) => (
                        <StyledListboxOption key={`${option}-${i}`} value={option.value}>
                            <ItemText>{option.label}</ItemText>
                        </StyledListboxOption>
                    ))}
                </StyledViewport>
            </StyledListboxPopover>
        </Portal>
    </Root>
)

export default Dropdown

const collisionPaddingStyles = {top: headerSizes.heightLarge, left: 16}

const Arrow = styled.span`
    display: block;
    transform: rotate(-180deg);

    &::after {
        content: url(${chevronUp});
    }
`

const StyledListboxButton = styled(Trigger)`
    display: inline-flex;
    text-align: left;
    cursor: pointer;
    user-select: none;
    background: none;
    border: none;
    align-items: center;
    justify-content: space-between;
    gap: ${tokens.spacing20};

    &:focus-within {
        ${focusStyles()};
        border-radius: ${tokens.arc4};
    }

    &[data-state='open'] ${Arrow} {
        transform: rotate(0);
    }
`

const StyledListboxPopover = styled(Content)`
    min-width: 320px;
    max-height: var(--radix-select-content-available-height);
    background-color: ${colors.background.default};
    border-radius: ${radii.arc8};

    &[data-state='open'] {
        ${focusStyles()}
    }

    &[hidden] {
        display: none;
    }
`

const StyledViewport = styled(Viewport)`
    display: grid;
    gap: ${tokens.spacing20};
    padding: ${tokens.spacing20};
`

const StyledListboxOption = styled(Item)`
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    ${text({variant: 'paragraph-large'})}

    &[data-highlighted],
    &[data-state='checked'] {
        color: ${colors.text.brand};
    }
`
